/**
 * AI subtitle generation service.
 *
 * Sibling of trailerService.js: where that one designs + renders a trailer, this
 * one asks the analyze server to TRANSCRIBE a video into timed subtitles (and,
 * optionally, translate them into other languages). It reuses the same analyze
 * payload builder so File uploads and library/CDN videos are resolved
 * identically, then adds the subtitle-only param (target languages).
 *
 * Like trailer rendering there is no mock fallback — transcription needs the real
 * server (Gemini + ffmpeg). If REACT_APP_GEMINI_ANALYZE_URL isn't set the caller
 * gets a clear error rather than fake captions.
 *
 * The result is a list of { language, format:'srt', url } — the SAME shape the
 * CMS already uses for manually-uploaded subtitle files — so a generated set can
 * prefill the existing subtitle rows and be saved through the normal path.
 *
 * Requires REACT_APP_GEMINI_ANALYZE_URL to point at the analyze server
 * (e.g. http://localhost:3002/api/v1).
 */

import axios from "axios";
import buildVideoAnalyzePayload, {
  VideoSourceError,
} from "./gemini/buildAnalyzeRequest";

const GEMINI_BASE_URL = process.env.REACT_APP_GEMINI_ANALYZE_URL || "";

export { VideoSourceError };

/** Stages surfaced to the UI while a subtitle job runs (server `stage` ids map onto these). */
export const SUBTITLE_STAGES = [
  { id: "uploading", label: "Uploading" },
  { id: "extracting_audio", label: "Extracting audio" },
  { id: "transcribing", label: "Transcribing" },
  { id: "building_subtitles", label: "Building subtitles" },
];

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

const requireServer = () => {
  if (!GEMINI_BASE_URL) {
    throw new Error(
      "AI subtitle generation isn't configured. Set REACT_APP_GEMINI_ANALYZE_URL to the analyze server."
    );
  }
  return GEMINI_BASE_URL.replace(/\/$/, "");
};

/**
 * Attach the target-language list onto the analyze payload (multipart or JSON).
 *
 * `diarize` asks the transcript to label WHO speaks each line. Subtitles don't need
 * that, so it's off unless a dub is queued for the same video — in which case one
 * diarized transcription serves both jobs and the pair costs a single AI request
 * instead of two.
 */
const withSubtitleParams = (payload, { targetLanguages, diarize, transcribeLanguages }) => {
  const langs = Array.isArray(targetLanguages)
    ? targetLanguages.filter(Boolean).join(",")
    : "";
  // Extra languages to translate DURING transcription without producing .srt files for
  // them — the dub's languages. Without this the dub would find no text to speak in the
  // shared transcript and would have to transcribe all over again.
  const extraLangs = Array.isArray(transcribeLanguages)
    ? transcribeLanguages.filter(Boolean).join(",")
    : "";
  if (payload.type === "multipart") {
    if (langs) payload.body.append("target_languages", langs);
    if (extraLangs) payload.body.append("transcribe_languages", extraLangs);
    if (diarize) payload.body.append("diarize", "true");
    return payload;
  }
  return {
    ...payload,
    body: {
      ...payload.body,
      target_languages: langs || null,
      ...(extraLangs ? { transcribe_languages: extraLangs } : {}),
      ...(diarize ? { diarize: true } : {}),
    },
  };
};

const startSubtitleJob = async (payload) => {
  const url = `${requireServer()}/videos/generate-subtitles`;
  const config =
    payload.type === "multipart"
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : {};
  const response = await axios.post(url, payload.body, config);
  const data = response.data?.data ?? response.data;
  return data?.job_id || data?.jobId;
};

const fetchSubtitleStatus = async (jobId) => {
  const url = `${requireServer()}/videos/generate-subtitles/${jobId}`;
  const response = await axios.get(url);
  return response.data?.data ?? response.data;
};

const pollSubtitleJob = async ({ jobId, onStage, signal }) => {
  const pollMs = 2000;

  while (true) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const status = await fetchSubtitleStatus(jobId);
    if (status?.stage) onStage?.(status.stage, status.progress ?? 0);

    if (status?.status === "failed") {
      throw new Error(status.error || "Subtitle generation failed. Please try again.");
    }
    if (status?.status === "complete") {
      onStage?.("complete", 100);
      return status;
    }

    await wait(pollMs, signal);
  }
};

/**
 * Generate subtitles for a video.
 *
 * @param {object}   params
 * @param {object}   params.video              File or library/CDN video descriptor.
 * @param {string[]} [params.targetLanguages]  ISO 639-1 codes to translate into (besides the original).
 * @param {function} [params.onStage]          (stageId, pct) => void progress callback.
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{ subtitles: Array<{language:string,format:string,url:string,cueCount:number,translated:boolean}>, language:string|null, warnings:string[] }>}
 */
export const generateSubtitles = async ({
  video,
  targetLanguages = [],
  /** Ask for speaker labels — set when a dub is queued for the same video. */
  diarize = false,
  /**
   * Extra languages to translate while transcribing, WITHOUT writing .srt files for
   * them — the dub's languages, so one transcript serves both jobs.
   */
  transcribeLanguages = [],
  onStage,
  signal,
} = {}) => {
  const base = buildVideoAnalyzePayload(video);
  if (!base) throw new Error("No video selected for subtitle generation.");

  const payload = withSubtitleParams(base, {
    targetLanguages,
    diarize,
    transcribeLanguages,
  });

  onStage?.(SUBTITLE_STAGES[0].id, 0);

  const jobId = await startSubtitleJob(payload);
  if (!jobId) throw new Error("Subtitle API did not return a job id.");

  const finished = await pollSubtitleJob({ jobId, onStage, signal });
  const subtitles = finished.result?.subtitles;
  if (!Array.isArray(subtitles) || subtitles.length === 0) {
    throw new Error("Subtitle job finished without producing any subtitles.");
  }

  return {
    subtitles,
    language: finished.result?.language ?? null,
    warnings: finished.result?.warnings || [],
  };
};

export default generateSubtitles;
